import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";

import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";

import { CoreConfigService } from "@core/services/config.service";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { AuthenticationService } from "../../../api/services/authentication.service";
import { TranslateService } from "@ngx-translate/core";
import { PasswordsMatchValidator } from "../../../util/validator/passwords-match.validator";
import { LogoService } from "../../../util/service/logo.service";

@Component({
    selector: "app-auth-reset-password-v1",
    templateUrl: "./auth-reset-password-v1.component.html",
    styleUrls: ["./auth-reset-password-v1.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AuthResetPasswordV1Component implements OnInit {
  // Public
  public coreConfig: any;
  public passwordTextType: boolean;
  public confPasswordTextType: boolean;
  public resetPasswordForm: UntypedFormGroup;
  public submitted = false;

  // Private
  private _unsubscribeAll: Subject<any>;
  private token;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   * @param {FormBuilder} _formBuilder
   * @param route
   * @param _authenticationService
   * @param _router
   * @param toastrService
   * @param translate
   * @param logoService
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private _formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private _authenticationService: AuthenticationService,
    private _router: Router,
    private toastrService: ToastrService,
    private translate: TranslateService,
    public logoService: LogoService
  ) {
    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.resetPasswordForm.controls;
  }

  /**
   * Toggle password
   */
  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  /**
   * Toggle confirm password
   */
  toggleConfPasswordTextType() {
    this.confPasswordTextType = !this.confPasswordTextType;
  }

  /**
   * On Submit
   */
  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.resetPasswordForm.invalid) {
      return;
    }

    if (!this.token) {
      return;
    }
    this._authenticationService
      .authControllerResetPassword({
        body: {
          newPassword: this.resetPasswordForm.value.newPassword,
          resetToken: this.token,
        },
      })
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translate.instant("AUTH.PASSWORD.RESET_SUCCESS_MESSAGE"),
            this.translate.instant("AUTH.PASSWORD.RESET_SUCCESS_TITLE")
          );
          this._router.navigate(["/login"]);
        },
        error: (err) => console.error(err),
      });
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get("token");
    this.resetPasswordForm = this._formBuilder.group({
      newPassword: ["", [Validators.minLength(5), Validators.required]],
      confirmPassword: ["", [Validators.required]],
    });
    this.resetPasswordForm.setValidators([PasswordsMatchValidator.createValidator()]);

    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.coreConfig = config;
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
